import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import MobileOffcanvas from "./MobileOffcanvas";
import {
  toggleMobileOffcanvas,
  closeMobileOffcanvas,
  selectIsMobileOffcanvasOpen,
  openHeaderActionsOffcanvas,
} from "../../headerUiSlice";
import type { AppDispatch } from "../../../../app/store";

// Simple Hamburger Icon SVG
const HamburgerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mobile-action-icon hamburger-icon"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

// Search Icon SVG
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mobile-action-icon search-icon"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const MobileActionsContainer: React.FC = () => {
  const { t, ready: translationsReady } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isMobileOffcanvasOpen = useSelector(selectIsMobileOffcanvasOpen);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  const handleToggleMobileMenu = () => {
    dispatch(toggleMobileOffcanvas());
  };

  const handleCloseMobileMenu = () => {
    dispatch(closeMobileOffcanvas());
  };

  const handleOpenSearchOffcanvas = () => {
    dispatch(openHeaderActionsOffcanvas("search"));
    dispatch(closeMobileOffcanvas());
  };

  useEffect(() => {
    const targetElement = document.getElementById("portal-mobile-menu-trigger");
    setPortalTarget(targetElement);
  }, []);

  useEffect(() => {
    const desktopBreakpoint = 1024;
    const mediaQuery = window.matchMedia(
      `(min-width: ${desktopBreakpoint + 1}px)`
    );

    const handleViewportChange = () => {
      if (mediaQuery.matches && isMobileOffcanvasOpen) {
        dispatch(closeMobileOffcanvas());
      }
    };

    handleViewportChange();
    mediaQuery.addEventListener("change", handleViewportChange);
    return () => {
      mediaQuery.removeEventListener("change", handleViewportChange);
    };
  }, [isMobileOffcanvasOpen, dispatch]);

  if (!translationsReady) {
    return null;
  }

  const hamburgerButton = (
    <button
      className="mobile-action-button mobile-menu-toggle"
      aria-label={t("header.toggleMobileMenu", "Toggle mobile menu")}
      aria-expanded={isMobileOffcanvasOpen}
      onClick={handleToggleMobileMenu}
    >
      <HamburgerIcon />
    </button>
  );

  return (
    <>
      <div className="mobile-actions">
        {/* Search Button */}
        <button
          className="mobile-action-button mobile-search-toggle"
          aria-label={t("common.search")}
          onClick={handleOpenSearchOffcanvas}
        >
          <SearchIcon />
        </button>
      </div>

      {portalTarget && createPortal(hamburgerButton, portalTarget)}

      <MobileOffcanvas
        isOpen={isMobileOffcanvasOpen}
        onClose={handleCloseMobileMenu}
      />
    </>
  );
};

export default MobileActionsContainer;
